'use client';

/** 简洁 SVG 双曲线图：策略权益 vs 基准（支持浅/深主题 CSS 变量） */
import type { EquityPoint } from '@/lib/strategy';

interface Props {
  series: Array<{ label: string; data: EquityPoint[]; color: string; dashed?: boolean }>;
  height?: number;
}

export default function StrategyChart({ series, height = 260 }: Props) {
  const nonEmpty = series.filter((s) => s.data.length > 1);
  if (nonEmpty.length === 0) return null;

  const W = 720;
  const H = Math.max(150, height);
  // 顶部预留图例行，避免与 X 轴日期重叠
  const PAD = { l: 8, r: 12, t: 34, b: 24 };

  let min = Infinity;
  let max = -Infinity;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const s of nonEmpty) {
    for (const p of s.data) {
      if (p.equity < min) min = p.equity;
      if (p.equity > max) max = p.equity;
      if (p.t < tMin) tMin = p.t;
      if (p.t > tMax) tMax = p.t;
    }
  }
  if (!isFinite(min) || !isFinite(max)) return null;
  const span = max - min;
  if (span <= 0) { max = min + 1; }
  const padY = (max - min) * 0.08;

  const x = (t: number) => PAD.l + ((t - tMin) / Math.max(1, tMax - tMin)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - min + padY) / (max - min + padY * 2)) * (H - PAD.t - PAD.b);

  const path = (data: EquityPoint[]) => data.map((p, i) => (i === 0 ? 'M' : 'L') + x(p.t).toFixed(1) + ',' + y(p.equity).toFixed(1)).join(' ');

  // X 轴刻度（最多 5 个）
  const ticks: Array<{ t: number; label: string }> = [];
  const n = 5;
  for (let i = 0; i < n; i++) {
    const t = tMin + (tMax - tMin) * (i / (n - 1));
    const d = new Date(t);
    const mon = String(d.getUTCMonth() + 1).padStart(2, '0');
    ticks.push({ t, label: d.getUTCFullYear() + '-' + mon });
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="equity curve" className="block">
      {/* 网格 */}
      {[0.25, 0.5, 0.75].map((r) => (
        <line key={r} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + r * (H - PAD.t - PAD.b)} y2={PAD.t + r * (H - PAD.t - PAD.b)} stroke="currentColor" strokeOpacity={0.08} />
      ))}
      {ticks.map((tk, i) => (
        <text key={i} x={x(tk.t)} y={H - 6} fontSize={10} fill="currentColor" fillOpacity={0.45} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
          {tk.label}
        </text>
      ))}
      {/* 零线（初始资金基准） */}
      {nonEmpty.map((s) => {
        if (s.data.length < 2) return null;
        return null;
      })}
      {nonEmpty.map((s) => (
        <path key={s.label} d={path(s.data)} fill="none" style={{ stroke: s.color, strokeDasharray: s.dashed ? '5 4' : undefined }} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      ))}
      {/* 图例（顶部） */}
      <g transform={`translate(${PAD.l},10)`}>
        {nonEmpty.map((s, i) => (
          <g key={s.label} transform={`translate(${i * 190},0)`}>
            <line x1={0} y1={0} x2={16} y2={0} style={{ stroke: s.color, strokeDasharray: s.dashed ? '5 4' : undefined }} strokeWidth={2} />
            <text x={22} y={3.5} fontSize={11} fill="currentColor" fillOpacity={0.75}>{s.label}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}
